/// <amd-module name="Core/Medius.Core.Web/Scripts/components/sendEmailDialog/emailAddressMultiSelectInput"/>
import * as React from "react";
import { Contact, Recipient } from "./types";
import { MultiSelect, MultiSelectChangeEvent, MultiSelectFilterChangeEvent, TagData } from "@progress/kendo-react-dropdowns";
import { FlexContent, FlexLayout, IconCommunicationRequiredRegular, IconStatusWarningRegular, TokenCssVariables } from "@medius/ui-controls";
import { translate, getLabelTranslation } from "Core/Medius.Core.Web/Scripts/lib/globalization";
import { isNullOrEmpty, validateEmail, validateEmails } from "./utils";
import { ErrorItem } from "./errorItem";
import { filterBy } from "@progress/kendo-data-query";

export interface EmailAddressMultiSelectInputProps {
    inputLabel: string;
    isRequired?: boolean;
    addressBook: Contact[];
    recipients: Recipient[];
    onChange: (event: MultiSelectChangeEvent) => void;
    className?: string;
}

export const EmailAddressMultiSelectInput = (props: EmailAddressMultiSelectInputProps) => {
    const { inputLabel, isRequired = false, addressBook, recipients, onChange} = props;
    const [data, setData] = React.useState(addressBook);

    const onFilterChange = (event: MultiSelectFilterChangeEvent) => {
        setData(filterBy(addressBook.slice(), event.filter));
    };

    const emailChipContainerStyles = { height: "14px", display: "flex" };

    const renderRedEmailAddress = (recipient: Recipient, li:React.ReactElement<HTMLLIElement, string | React.JSXElementConstructor<any>>) => {
        return React.cloneElement(
        li,
        {
            ...li.props,
            style: {
                ...li.props.style,
                backgroundColor: TokenCssVariables.ColorErrorBackground,
            },
        },
        [
            <div key={`item-${recipient.emailAddress}`} style={emailChipContainerStyles}>
                <IconStatusWarningRegular key={`icon-${recipient.emailAddress}`} style={{paddingRight: "4px"}} />
                <span key={`span-${recipient.emailAddress}`}>{recipient.displayName}</span>
            </div>,
            li.props.children
        ] as any[]);
    };

    const renderGreenEmailAddress = (recipient: Recipient, li:React.ReactElement<HTMLLIElement, string | React.JSXElementConstructor<any>>) => {
        return React.cloneElement(
        li,
        {
            ...li.props,
            style: {
                ...li.props.style,
                backgroundColor: TokenCssVariables.ColorOkBackground,
            },
        },
        [
            <div key={`item-${recipient.emailAddress}`} style={emailChipContainerStyles}>
                <span key={`span-${recipient.emailAddress}`}>{recipient.displayName}</span>
            </div>,
            li.props.children
        ] as any[]);
    };

    const renderEmailAdress = (recipient: Recipient, li: React.ReactElement<HTMLLIElement, string | React.JSXElementConstructor<any>>) => {
        if(recipient.deliveryFailed || !validateEmail(recipient.emailAddress))
            return renderRedEmailAddress(recipient, li);

        return renderGreenEmailAddress(recipient, li);
    };

    const tagRender = (
        tagData: TagData,
        li: React.ReactElement<HTMLLIElement, string | React.JSXElementConstructor<any>>
    ) => renderEmailAdress(tagData.data[0] as Recipient, li);

    return (
        <FlexLayout spacerMode="on" spacerSize="small" width="fill">
            <FlexContent>
                <fieldset>
                    <div>{inputLabel}: {isRequired && isNullOrEmpty(recipients) && <IconCommunicationRequiredRegular size="vsmall" color="error" aria-hidden={true} />}</div>
                    <MultiSelect
                        filterable={true}
                        onFilterChange={onFilterChange}
                        data={data}
                        onChange={onChange}
                        value={recipients}
                        size="large"
                        allowCustom={true}
                        textField="displayName"
                        dataItemKey="emailAddress"
                        tagRender={tagRender}
                        className={props.className ? props.className : "email-address-multi-select"}
                    />
                    {isRequired && isNullOrEmpty(recipients) && <ErrorItem errorMessage={getLabelTranslation("#Core/formFieldRequiredErrorMessage", "Recipients field")} />}
                    {!validateEmails(recipients) && <ErrorItem errorMessage={translate("#Core/defaultValidation_EMAILATTRIBUTE")} />}
                </fieldset>
            </FlexContent>
        </FlexLayout>
    );
};